import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Fuel, Gauge, Settings, MapPin, Phone, Mail, Heart, Share2, Eye } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../admin/lib/supabaseClient';
import type { CarListing } from '../admin/types';
import toast from 'react-hot-toast';
import CarDetailModal from './CarDetailModal';
import { trackCarInquiry } from '../utils/analytics';

// Small rotating card used in hero to showcase a single mini and rotate every few seconds
function RotatingMiniCard({ cars, onView }: { cars: any[]; onView: (c: any) => void }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!cars || cars.length === 0) return;
    setIndex(0);
    const t = setInterval(() => setIndex(i => (i + 1) % cars.length), 4000);
    return () => clearInterval(t);
  }, [cars]);

  const car = cars && cars.length ? cars[index] : null;

  if (!car) {
    return (
      <div className="bg-white/10 p-4 rounded-xl border border-white/20">
        <div className="h-36 flex items-center justify-center text-white/80">No minis yet</div>
      </div>
    );
  }

  const formatPrice = (price: number) => `R ${(price / 100).toLocaleString()}`;

  return (
    <div className="bg-white p-4 rounded-xl shadow-lg cursor-pointer" onClick={() => onView(car)}>
      <div className="relative overflow-hidden rounded-md">
        {car.image_url ? (
          <img
            src={car.image_url}
            alt={`${car.make} ${car.model}`}
            className="w-full h-44 object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-44 bg-gray-200 flex items-center justify-center text-gray-500">No Image</div>
        )}
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${car.status === 'available' ? 'text-green-700 bg-green-100' : 'text-gray-700 bg-gray-100'}`}>
          {car.status.replace('-', ' ').toUpperCase()}
        </div>
      </div>

      <div className="mt-3 text-left">
        <h3 className="text-lg font-bold text-gray-900">{car.make} {car.model}</h3>
        <p className="text-sm text-gray-600">{car.year} • {car.color}</p>
        <div className="mt-2 text-mini-red font-bold text-xl">{formatPrice(car.price)}</div>
      </div>
    </div>
  );
}

interface CarInquiryData {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  message: string;
  inquiry_type: 'general' | 'viewing' | 'offer' | 'financing';
}

export default function MiniSales() {
  const [cars, setCars] = useState<CarListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState<CarListing | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    make: '',
    maxPrice: ''
  });

  useEffect(() => {
    loadCars();
  }, []);

  // Deep-link: open detail modal if URL hash references a car id
  useEffect(() => {
    if (!window || !window.location) return;
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    // wait until cars are loaded
    const tryOpen = () => {
      const target = cars.find(c => c.id === hash);
      if (target) {
        setSelectedCar(target);
        setShowDetailModal(true);
        // remove hash to avoid reopening
        try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) { }
      }
    };
    if (!loading) tryOpen();
    const t = setInterval(() => { if (!loading) { tryOpen(); clearInterval(t); } }, 300);
    return () => clearInterval(t);
  }, [cars, loading]);

  const loadCars = async () => {
    setLoading(true);
    try {
      if (!isSupabaseConfigured) {
        // Load sample data if Supabase not configured
        const sampleCars: CarListing[] = [
          {
            id: '1',
            make: 'Mini',
            model: 'Cooper',
            year: 2018,
            price: 25000000,
            mileage: 45000,
            fuel_type: 'petrol',
            transmission: 'manual',
            engine_size: '1.5L',
            color: 'British Racing Green',
            condition: 'excellent',
            description: 'Beautiful Mini Cooper in excellent condition. One owner, full service history.',
            features: [],
            status: 'available',
            location: 'Cape Town',
            contact_name: 'Mike',
            contact_phone: '+27 82 123 4567',
            is_featured: true,
            created_at: new Date().toISOString()
          }
        ];
        setCars(sampleCars);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('car_listings')
        .select('*')
        .in('status', ['available', 'reserved', 'under-offer', 'upcoming', 'under-consignment'])
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCars(data || []);
    } catch (error) {
      toast.error('Failed to load cars for sale');
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return `R ${(price / 100).toLocaleString()}`;
  };

  const formatMileage = (mileage?: number) => {
    if (!mileage) return 'N/A';
    return `${mileage.toLocaleString()} km`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'reserved': return 'text-yellow-600 bg-yellow-100';
      case 'under-offer': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleCarClick = (car: CarListing) => {
    setSelectedCar(car);
    setShowDetailModal(true);
  };

  const handleInquiry = (car: CarListing) => {
    setSelectedCar(car);
    setShowInquiryModal(true);
    // Track car inquiry initiation
    trackCarInquiry(car.id, `${car.make} ${car.model} (${car.year})`);
  };

  const filteredCars = cars.filter(car => {
    return (!filters.make || car.make.toLowerCase().includes(filters.make.toLowerCase())) &&
      (!filters.maxPrice || car.price <= parseInt(filters.maxPrice) * 100);
  });

  const InquiryModal = ({ car }: { car: CarListing }) => {
    const [formData, setFormData] = useState<CarInquiryData>({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      message: '',
      inquiry_type: 'general'
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        if (isSupabaseConfigured) {
          const { error } = await supabase
            .from('car_inquiries')
            .insert([{
              car_listing_id: car.id,
              ...formData
            }]);

          if (error) throw error;
        }

        toast.success('Inquiry sent successfully! We\'ll get back to you soon.');
        setShowInquiryModal(false);
        setFormData({ customer_name: '', customer_email: '', customer_phone: '', message: '', inquiry_type: 'general' });
      } catch (error) {
        toast.error('Failed to send inquiry. Please try again or call us directly.');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <h3 className="text-xl font-bold mb-4">Inquire About {car.make} {car.model}</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
              <select
                value={formData.inquiry_type}
                onChange={(e) => setFormData(prev => ({ ...prev, inquiry_type: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              >
                <option value="general">General Inquiry</option>
                <option value="viewing">Schedule Viewing</option>
                <option value="offer">Make an Offer</option>
                <option value="financing">Financing Options</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
                placeholder="Tell us more about your interest in this vehicle..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowInquiryModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-mini-red text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Inquiry'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="min-h-[80vh] md:min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-mini-red to-red-700 text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Mini Sales</h1>
              <p className="text-xl md:text-2xl opacity-90 mb-4">Find Your Perfect Mini Cooper</p>
              <p className="text-lg opacity-80 max-w-2xl">
                Discover our curated collection of classic and modern Mini Coopers. Each vehicle is carefully inspected and comes with our quality guarantee.
              </p>
            </div>

            {/* Right-side rotating featured mini card */}
            <div className="mx-auto w-full max-w-md">
              <RotatingMiniCard cars={cars} onView={(car) => { setSelectedCar(car); setShowDetailModal(true); }} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make/Model</label>
              <input
                type="text"
                placeholder="Search by make or model"
                value={filters.make}
                onChange={(e) => setFilters(prev => ({ ...prev, make: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Price</label>
              <select
                value={filters.maxPrice}
                onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              >
                <option value="">Any Price</option>
                <option value="200000">Under R200,000</option>
                <option value="300000">Under R300,000</option>
                <option value="400000">Under R400,000</option>
                <option value="500000">Under R500,000</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Cars Grid */}
      <section className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mini-red"></div>
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No cars found</h3>
            <p className="text-gray-500">Try adjusting your filters or check back later for new listings.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCars.map((car) => (
              <motion.div
                key={car.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                onClick={() => handleCarClick(car)}
              >
                {car.is_featured && (
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-sm font-medium px-3 py-1 text-center">
                    Featured Vehicle
                  </div>
                )}

                <div className="relative">
                  {car.image_url ? (
                    <img
                      src={car.image_url}
                      alt={`${car.make} ${car.model}`}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400">No Image</span>
                    </div>
                  )}

                  <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(car.status)}`}>
                    {car.status.replace('-', ' ').toUpperCase()}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{car.make} {car.model}</h3>
                      <p className="text-gray-600">{car.year} • {car.color}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-mini-red">{formatPrice(car.price)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Gauge size={16} />
                      <span>{formatMileage(car.mileage)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{car.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Fuel size={16} />
                      <span className="capitalize">{car.fuel_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Settings size={16} />
                      <span className="capitalize">{car.transmission}</span>
                    </div>
                  </div>

                  {car.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <MapPin size={16} />
                      <span>{car.location}</span>
                    </div>
                  )}

                  <p className="text-gray-700 text-sm mb-4 line-clamp-2">{car.description}</p>

                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInquiry(car);
                      }}
                      className="flex-1 bg-mini-red text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Inquire Now
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCarClick(car);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Inquiry Modal */}
      {showInquiryModal && selectedCar && (
        <InquiryModal car={selectedCar} />
      )}

      {/* Car Detail Modal */}
      {showDetailModal && selectedCar && (
        <CarDetailModal
          car={selectedCar}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            // clear any hash if present
            try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) { }
          }}
          onInquire={(car) => {
            setSelectedCar(car);
            setShowInquiryModal(true);
            setShowDetailModal(false);
          }}
        />
      )}
    </div>
  );
}
